"use client";

import { type ReactNode, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Alert } from "../../elements";
import { Button } from "../../elements/button/button";
import { Image } from "../../elements/image/image";
import { Text } from "../../elements/text/text";
import type { ProductInputQuantityProps } from "../product-input-quantity/product-input-quantity";
import { ProductInputQuantity } from "../product-input-quantity/product-input-quantity";
import { ProductPriceInfo } from "./product-price-info";

export type ProductSizeNotice = {
  title: string;
};

export type ProductSizeOption = {
  size: string;
  productId: string;
  ean?: string;
  quantityProps: Omit<ProductInputQuantityProps, "size" | "productId" | "ean">;
  notice?: ProductSizeNotice;
  priceInfo?: PriceInfo;
};

type AlertStatus = {
  isVisible: boolean;
  maxAvailability: number;
};

export type PriceInfo = {
  label: string;
  value: string;
  showRrp?: boolean;
  /** Optional label for the RRP block (e.g. 'RRP') */
  rrpLabel?: string;
  rrpValue?: string;
  discountLabel?: string;
  // Optional discounted price shown below the original/your price
  discountPrice?: string;
  // Indicates the price is net-priced (no tax included)
  isNetPriced?: boolean;
  // Control whether to show the variant price label and value (defaults to true)
  showVariantPrice?: boolean;
};

export type ProductInfo = {
  imageUrl: string;
  imageAlt: string;
  colorName?: string;
  lensName?: string;
  productName?: string;
  productCode?: string;
  href?: string;
};

export interface ProductSizesProps {
  country?: string;
  productInfo?: ProductInfo;
  priceInfo?: PriceInfo;
  /** Optional label text to render when price is net-priced (e.g. "NET") */
  tagNetPriced?: string;
  sizes: ProductSizeOption[];
  // When provided, overrides each size's `quantityProps.showAvailability`
  showAvailability?: boolean;
  showEanCode?: boolean;
  className?: string;
  microcopies: {
    orderLimitReachedTitle: string;
    orderLimitReachedDescription: string;
    fromLabel?: string;
  };
  microcopyLabels?: {
    frameLabel?: string;
    lensLabel?: string;
  };
  /** Distinct count of color/lens attribute values across all variants */
  attributeCounts?: {
    colorCount?: number;
    lensCount?: number;
  };
  handleButtonClick: (href: string) => void;
  onProductStateChange?: (productId: string, isModified: boolean) => void;
  /** Keyed by size `productId`; a size is reported to `onSizeInView` at most once per map instance. */
  seenMap?: Map<string, { productId: string }>;
  onSizeInView?: (size: ProductSizeOption) => void;
}

function SizeViewTracker(
  props: Readonly<{
    sizeOption: ProductSizeOption;
    seenMap?: Map<string, { productId: string }>;
    onSizeInView?: (size: ProductSizeOption) => void;
    children: ReactNode;
  }>
) {
  const { ref, inView } = useInView({ threshold: 1.0, triggerOnce: true });
  const internalSeenMapRef = useRef<Map<string, { productId: string }>>(
    new Map()
  );
  const seenMap = props.seenMap ?? internalSeenMapRef.current;

  if (inView && !seenMap.has(props.sizeOption.productId)) {
    seenMap.set(props.sizeOption.productId, {
      productId: props.sizeOption.productId,
    });
    props.onSizeInView?.(props.sizeOption);
  }

  return (
    <div className="flex flex-col items-start gap-1 pb-2" ref={ref}>
      {props.children}
    </div>
  );
}

export function ProductSizes(props: Readonly<ProductSizesProps>): ReactNode {
  const [alertStatus, setAlertStatus] = useState<AlertStatus>({
    isVisible: false,
    maxAvailability: 0,
  });

  return (
    <div className={props.className}>
      {alertStatus.isVisible && (
        <div className="mb-4">
          <Alert
            microcopies={{
              title: props.microcopies.orderLimitReachedTitle,
              description:
                props.microcopies.orderLimitReachedDescription.replace(
                  "{EffectiveLimit}",
                  alertStatus.maxAvailability.toString()
                ),
            }}
            variant="warning"
          />
        </div>
      )}
      <div className="flex flex-col items-start gap-6 md:flex-row">
        {/* Product Image and Info */}
        {props.productInfo && (
          <Button
            className="flex h-full w-[280px] min-w-0 justify-start gap-4 px-0 py-4 hover:no-underline min-[1024px]:max-[1070px]:w-[230px]"
            href={props.productInfo.href || ""}
            onClick={(
              e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
            ) => {
              // Only handle clicks when a non-empty href is present
              e.preventDefault();
              props.handleButtonClick(props?.productInfo?.href || "");
            }}
            unstyled={true}
            variant="link"
          >
            <div className="h-16 w-16 shrink-0 overflow-hidden">
              <Image
                alt={props.productInfo.imageAlt}
                aspectRatio="1:1"
                borderRadius="sm"
                fill={true}
                objectFit="cover"
                src={props.productInfo.imageUrl}
              />
            </div>

            {/* Price Information moved under product name */}
            {props.priceInfo && (
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                {/* product name / color and lens */}
                {(props.productInfo?.colorName ||
                  props.productInfo?.lensName ||
                  props.productInfo?.productName) && (
                  <div className="w-full min-w-0 [&>p]:whitespace-normal [&>p]:break-words">
                    {/* Conditional color rendering based on distinct color count */}
                    {props.productInfo?.colorName &&
                      (() => {
                        // const colorCount = props.attributeCounts?.colorCount ?? 0;
                        // If 2 distinct values, show label + value
                        if (
                          props.productInfo.colorName &&
                          props.productInfo.lensName
                        ) {
                          return (
                            <Text color="black" variant="body-sm-bold">
                              {`${props.microcopyLabels?.frameLabel}: ${props.productInfo.colorName}`}
                            </Text>
                          );
                        }
                        // For all other cases (1, 0, 3+), show value only without label
                        return (
                          <Text color="black" variant="body-sm-bold">
                            {props.productInfo.colorName}
                          </Text>
                        );
                      })()}
                    {/* Conditional lens rendering based on distinct lens count */}
                    {props.productInfo?.lensName &&
                      (() => {
                        // const lensCount = props.attributeCounts?.lensCount ?? 0;
                        // If 2 distinct values, show label + value
                        if (
                          props.productInfo.colorName &&
                          props.productInfo.lensName
                        ) {
                          return (
                            <Text color="black" variant="body-sm-bold">
                              {`${props.microcopyLabels?.lensLabel}: ${props.productInfo.lensName}`}
                            </Text>
                          );
                        }
                        // For all other cases (1, 0, 3+), show value only without label
                        return (
                          <Text color="black" variant="body-sm-bold">
                            {props.productInfo.lensName}
                          </Text>
                        );
                      })()}
                    {!(
                      props.productInfo?.colorName ||
                      props.productInfo?.lensName
                    ) &&
                      props.productInfo?.productName && (
                        <Text color="black" variant="body-sm-bold">
                          {props.productInfo.productName}
                        </Text>
                      )}
                  </div>
                )}
                {props.priceInfo && (
                  <ProductPriceInfo
                    priceInfo={props.priceInfo}
                    tagNetPriced={props.tagNetPriced}
                  />
                )}
              </div>
            )}
          </Button>
        )}

        {/* Right Side: Info, Price and Sizes */}
        <div className="min-w-0 flex-1">
          {/* Product name / color moved to image column */}

          {/* Price moved to left column beneath product info */}

          {/* Size Options - Horizontal Layout */}
          <div className="flex flex-wrap gap-4">
            {props.sizes.map((sizeOption) => (
              <SizeViewTracker
                key={`${sizeOption.size}-${sizeOption.productId}`}
                onSizeInView={props.onSizeInView}
                seenMap={props.seenMap}
                sizeOption={sizeOption}
              >
                <ProductInputQuantity
                  country={props.country}
                  {...sizeOption.quantityProps}
                  alertHandler={(status: AlertStatus) => setAlertStatus(status)}
                  ean={sizeOption.ean}
                  onProductStateChange={props.onProductStateChange}
                  productId={sizeOption.productId}
                  showAvailability={
                    props.showAvailability ??
                    sizeOption.quantityProps.showAvailability ??
                    false
                  }
                  showEanCode={props.showEanCode}
                  showProductId={sizeOption.productId.length > 0}
                  showSize={sizeOption.size.length > 0}
                  size={sizeOption.size}
                />

                {/* Per-variant price display */}
                {sizeOption.priceInfo && (
                  <div className="mt-1">
                    <ProductPriceInfo
                      priceInfo={sizeOption.priceInfo}
                      tagNetPriced={props.tagNetPriced}
                    />
                  </div>
                )}
              </SizeViewTracker>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
